/*
 *   Copyright (c) 2025 2023 Celadon Development LLC
 *   All rights reserved.
 *   Author Aliaksandr Ziabko <aliaksandr.ziabko@celadonsoft.com>
 */
import React, { useEffect, useState } from "react";
import { deleteTrainer, getTrainers } from "../../services/trainerService";
import AddButton from "../../components/AddButton";
import { useNavigate } from "react-router";
import DeleteModal from "../../components/DeleteModal";

interface Trainer {
  id: number;
  name: string;
  photoUrl?: string | null;
}

const TrainersList: React.FC = () => {
  const navigate = useNavigate();
  const [trainers, setTrainers] = useState<Trainer[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Trainer | null>(null);

  const fetchTrainers = async () => {
    const trainersList = await getTrainers();
    setTrainers(trainersList);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      await deleteTrainer(itemToDelete.id);
      setError(null);
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      fetchTrainers();
    } catch (err: any) {
      console.error(err);
      setError(
        err.response?.data?.message ||
          "Cannot delete trainer. It may be linked to a program."
      );
    }
  };

  useEffect(() => {
    fetchTrainers();
  }, []);

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Trainers</h1>
      <div className="mb-4 flex justify-end">
        <AddButton
          text="Add Trainer"
          onClick={() => navigate("/add-trainer")}
        />
      </div>

      {error && <div className="mb-4 text-red-600 font-semibold">{error}</div>}
      <div className="grid grid-cols-3 gap-4">
        {trainers.map((trainer) => (
          <div
            key={trainer.id}
            className="border p-4 rounded flex flex-col items-center"
          >
            {trainer.photoUrl && (
              <img
                src={trainer.photoUrl}
                alt={"Trainer"}
                className="w-24 h-24 rounded-full mb-2"
              />
            )}
            <div className="font-semibold">{trainer.name}</div>
            <div className="flex gap-2 mt-2">
              <button
                onClick={() =>
                  (window.location.href = `/edit-trainer/${trainer.id}`)
                }
                className="text-blue-600"
              >
                Edit
              </button>
              <button
                onClick={() => {
                  setItemToDelete(trainer);
                  setIsDeleteModalOpen(true);
                }}
                className="text-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        itemName={itemToDelete?.name || ""}
        itemType="trainers"
      />
    </div>
  );
};

export default TrainersList;
